import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Wordmark } from "@/components/Wordmark";
import PublicProfileView from "@/features/profiles/PublicProfileView";
import { fetchPublicProfile, recordPublicEvent } from "@/features/profiles/api/publicProfileApi";
import { downloadVCard } from "@/features/profiles/vcard";
import { validateSlug } from "@/features/cards/slug";
import { generateTheme } from "@/lib/theme/generateTheme";

function Unavailable() {
  return (
    <div data-testid="public-profile-unavailable" className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background px-6 text-center">
      <Wordmark to="/" />
      <h1 className="font-serif text-4xl tracking-tight">This profile isn't available.</h1>
      <p className="max-w-sm text-sm text-muted-foreground">The link may be incorrect, or the profile isn't public.</p>
      <Link to="/" data-testid="public-profile-home-link" className="mw-link text-sm">Go to MetWorth</Link>
    </div>
  );
}

function Ready({ slug, identity, themeColor }) {
  const theme = useMemo(() => generateTheme(themeColor), [themeColor]);
  const onSave = () => {
    downloadVCard(identity);
    recordPublicEvent(slug, "save_contact");
  };
  return (
    <div className="min-h-screen" style={{ background: theme.bgSecondary }}>
      <div className="mx-auto w-full max-w-[480px] md:py-10">
        <div className="min-h-screen overflow-hidden md:min-h-0 md:rounded-[28px] md:shadow-[0_30px_80px_-20px_rgba(0,0,0,0.45)]">
          <PublicProfileView identity={identity} theme={theme} onSaveContact={onSave} />
        </div>
      </div>
    </div>
  );
}

export default function PublicProfileRoute() {
  const slug = (useParams().slug || "").toLowerCase();
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    let active = true;
    if (validateSlug(slug)) {
      setState({ status: "unavailable" });
      return undefined;
    }
    setState({ status: "loading" });
    fetchPublicProfile(slug).then(
      (res) => {
        if (!active) return;
        if (!res) return setState({ status: "unavailable" });
        setState({ status: "ready", ...res });
        document.title = res.identity.fullName ? `${res.identity.fullName} · MetWorth` : "MetWorth";
        recordPublicEvent(slug, "profile_view");
      },
      () => active && setState({ status: "unavailable" }),
    );
    return () => {
      active = false;
    };
  }, [slug]);

  if (state.status === "loading") {
    return <div data-testid="public-profile-loading" className="flex min-h-screen items-center justify-center"><span className="mw-spinner" /></div>;
  }
  if (state.status === "unavailable") return <Unavailable />;
  return <Ready slug={slug} identity={state.identity} themeColor={state.themeColor} />;
}
